import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogActions, IconButton, FormControl, Select, MenuItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./user-management-modals.scss";

interface EditUserModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (user: any) => void;
    user: any;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ open, onClose, onSave, user }) => {
    const [formData, setFormData] = useState<any>(null);

    useEffect(() => {
        if (open && user) {
            setFormData({
                ...user,
                name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || "",
                status: user.status || (user.isActive ? "Active" : "Inactive")
            });
        }
    }, [open, user]);

    if (!formData) return null;

    const isChanged = formData && user && (
        formData.status !== (user.status || (user.isActive ? "Active" : "Inactive"))
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (isChanged) {
            onSave(formData);
        } else {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            className="user-management-modal-dialog"
        >
            <DialogTitle>
                <div className="modal-header">
                    <span>Edit User</span>
                    <IconButton onClick={onClose} size="small" className="close-btn">
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent>
                <form id="edit-user-form" onSubmit={handleSubmit} className="user-modal-form">
                    <div className="form-group">
                        <label>Name <span className="required">*</span></label>
                        <div className="input-wrapper">
                            <input
                                type="text"
                                placeholder="Enter full name"
                                value={formData.name || "-"}
                                disabled
                                className="disabled-input"
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        name: e.target.value
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Email <span className="required">*</span></label>
                        <div className="input-wrapper">
                            <input
                                type="email"
                                placeholder="Enter email address"
                                value={formData.email}
                                disabled
                                className="disabled-input"
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Role <span className="required">*</span></label>
                        <div className="input-wrapper">
                            <select
                                value={formData.role}
                                disabled
                                className="disabled-input"
                                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                            >
                                <option value="Admin">Admin</option>
                                <option value="Provider">Provider</option>
                                <option value="User">User</option>
                                <option value="Preneed Planner">Preneed Planner</option>
                            </select>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Status <span className="required">*</span></label>
                        <div className="input-wrapper">
                            <FormControl fullWidth>
                                <Select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    displayEmpty
                                    IconComponent={undefined}
                                    MenuProps={{
                                        PaperProps: {
                                            elevation: 2,
                                            sx: {
                                                mt: 0.5,
                                                borderRadius: '12px',
                                                border: '1px solid #f3f4f6',
                                                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                                                "& .MuiMenuItem-root": {
                                                    fontSize: '16px',
                                                    color: '#4b5563',
                                                    padding: '12px 16px',
                                                    fontFamily: "inherit",
                                                },
                                                "& .MuiMenuItem-root.Mui-selected": {
                                                    backgroundColor: "rgba(52, 92, 92, 0.08) !important",
                                                    color: "#345c5c",
                                                    fontWeight: 600,
                                                },
                                                "& .MuiMenuItem-root:hover": {
                                                    backgroundColor: "#f9fafb",
                                                }
                                            }
                                        }
                                    }}
                                    sx={{
                                        border: "1px solid #f3f4f6",
                                        borderRadius: "12px",
                                        backgroundColor: "#f9fafb",
                                        color: '#4b5563',
                                        fontSize: '16px',
                                        padding: '0px',
                                        fontFamily: "inherit",
                                        "& .MuiSelect-select": {
                                            padding: '14px 16px',
                                            paddingRight: '44px !important',
                                        },
                                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                        "&.Mui-focused": {
                                            border: "1px solid #345c5c",
                                            backgroundColor: "white",
                                            boxShadow: "0 0 0 4px rgba(52, 92, 92, 0.05)",
                                        },
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='C19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "right 16px center",
                                        backgroundSize: "20px",
                                    }}
                                >
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                </form>
            </DialogContent>
            <DialogActions>
                <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                <button type="submit" form="edit-user-form" className="save-butn" disabled={!isChanged}>Save Changes</button>
            </DialogActions>
        </Dialog>
    );
};

export default EditUserModal;
